<script setup>
/* global axios */
// Katalog urunleri: liste, arama, suzgec ve ayni sayfada acilan urun formu.
//
// Meta'ya bagimli degildir. Meta katalogu baglanmamis olsa da satici urun
// ekleyip yonetebilir; baglanti sadece senkronu acar.
//
// Fiyat sunucuda KURUS tamsayisidir, kullaniciya TL gosterilir. Cevrimi bu
// dosya yapar: kaydederken 100 ile carpar, formu acarken 100'e boler. Bu
// cevrimi kacirmak 100 kat yanlis fiyat demektir.
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

const { t } = useI18n();
const route = useRoute();

const uc = computed(() => `/api/v1/accounts/${route.params.accountId}/katalog`);

const urunler = ref([]);
const toplam = ref(0);
// Ilk yuklemede uyari yanip sonmesin diye bagli varsayiyoruz; yanit gelince
// gercek deger yazilir.
const metaBagli = ref(true);
// Hesaba bagli Meta katalogu (varsa). Bagliyken Meta'daki urunler de bu
// listede durur; ayri bir "Meta urunleri" ekrani kullaniciyi iki liste
// arasinda gezdirmek olurdu.
const metaKatalog = ref(null);

const yukleniyor = ref(true);
const hata = ref('');
const islemdeki = ref(null);

const arama = ref('');
const durumSuzgeci = ref('');

// Sunucu en fazla 500 urun dondurur; liste tam dolduysa kullaniciya daraltmasi
// gerektigini soylemek gerekir, yoksa eksik listeyi tam sanir.
const SUNUCU_SINIRI = 500;

const DURUMLAR = ['taslak', 'yayinda', 'arsiv'];
// Anahtarlar acik yazilir: `KATALOG.DURUM_${durum}` gibi uretilen anahtar
// aramalarda bulunamiyor ve eksik ceviri fark edilmeden kaliyor.
const DURUM_ANAHTARLARI = {
  taslak: 'KATALOG.DURUM_TASLAK',
  yayinda: 'KATALOG.DURUM_YAYINDA',
  arsiv: 'KATALOG.DURUM_ARSIV',
};
const DURUM_RENKLERI = {
  taslak: 'text-n-slate-11 bg-n-alpha-1',
  yayinda: 'text-n-teal-11 bg-n-alpha-1',
  arsiv: 'text-n-slate-11 bg-n-alpha-1',
};

const PARA_BIRIMLERI = ['TRY', 'USD', 'EUR'];

const bosForm = () => ({
  id: null,
  ad: '',
  kod: '',
  aciklama: '',
  fiyat: '',
  para_birimi: 'TRY',
  stok: '0',
  durum: 'taslak',
  gorsel_url: '',
  urun_url: '',
  marka: '',
  kategori: '',
});

const form = ref(bosForm());
const formAcik = ref(false);
const formHatasi = ref('');
const kaydediliyor = ref(false);

// Kirik gorsel adresleri: tarayicinin kirik resim simgesi yerine yer tutucu
// gosterebilmek icin hatali adresleri isaretliyoruz.
const bozukGorseller = ref([]);

const suzgecVar = computed(
  () => arama.value.trim() !== '' || durumSuzgeci.value !== ''
);

const hataMetni = e =>
  e?.response?.data?.error || e?.message || t('KATALOG.HATA_BILINMEYEN');

// --- fiyat cevrimleri ------------------------------------------------------

// Kurus -> forma yazilacak TL metni. Turkce girisle ayni bicimde olsun diye
// ondalik ayraci virgul.
const tlMetni = kurus => ((Number(kurus) || 0) / 100).toFixed(2).replace('.', ',');

// TL metni -> kurus tamsayisi. Gecersizse null doner (cagiran uyari gosterir).
// 19.99 * 100 kayan noktada 1998.9999... cikar; yuvarlamazsak 1 kurus kaybolur.
const kurusaCevir = metin => {
  const temiz = String(metin ?? '')
    .trim()
    .replace(/\s/g, '')
    .replace(',', '.');
  if (temiz === '') return 0;
  const sayi = Number(temiz);
  if (!Number.isFinite(sayi) || sayi < 0) return null;
  return Math.round(sayi * 100);
};

// Listede urunun kendi para biriminde gosteriyoruz: USD fiyati TL simgesiyle
// basmak yanlis rakam okutur.
const fiyatMetni = urun => {
  const birim = urun.para_birimi || 'TRY';
  const tutar = (Number(urun.fiyat_kurus) || 0) / 100;
  try {
    return new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency: birim,
    }).format(tutar);
  } catch (e) {
    // Tanimsiz para birimi kodu Intl'i patlatir; ham kodla gostermek yeter.
    return `${tutar.toFixed(2)} ${birim}`;
  }
};

const zamanMetni = deger => {
  if (!deger) return '';
  const tarih = new Date(deger);
  if (Number.isNaN(tarih.getTime())) return String(deger);
  return tarih.toLocaleString('tr-TR', { dateStyle: 'medium', timeStyle: 'short' });
};

const gorselVar = urun =>
  Boolean(urun.gorsel_url) && !bozukGorseller.value.includes(urun.gorsel_url);

const gorselHatasi = urun => {
  if (urun.gorsel_url && !bozukGorseller.value.includes(urun.gorsel_url)) {
    bozukGorseller.value = [...bozukGorseller.value, urun.gorsel_url];
  }
};

// --- veri ------------------------------------------------------------------

// Arama yazarken ust uste istek gider; gec donen eski yanit yeni listenin
// uzerine yazmasin diye her istege sira numarasi veriyoruz.
let istekSayaci = 0;

const yukle = async () => {
  const sira = (istekSayaci += 1);
  yukleniyor.value = true;
  hata.value = '';
  try {
    const { data } = await axios.get(uc.value, {
      params: {
        durum: durumSuzgeci.value || undefined,
        arama: arama.value.trim() || undefined,
      },
    });
    if (sira !== istekSayaci) return;
    urunler.value = data.urunler || [];
    toplam.value = data.toplam || 0;
    metaBagli.value = Boolean(data.meta_bagli);
    metaKatalog.value = data.meta_katalog || null;
  } catch (e) {
    if (sira !== istekSayaci) return;
    hata.value = hataMetni(e);
  } finally {
    if (sira === istekSayaci) yukleniyor.value = false;
  }
};

// Her tusa istek atmak sunucuyu gereksiz yorar; yazma durunca gidiyoruz.
const BEKLEME_MS = 300;
let zamanlayici = null;

watch(arama, () => {
  if (zamanlayici) clearTimeout(zamanlayici);
  zamanlayici = setTimeout(yukle, BEKLEME_MS);
});

// Suzgec tek tiklamayla degisir, beklemeye gerek yok.
watch(durumSuzgeci, yukle);

onBeforeUnmount(() => {
  if (zamanlayici) clearTimeout(zamanlayici);
});

onMounted(yukle);

// --- form ------------------------------------------------------------------

const yeniUrun = () => {
  form.value = bosForm();
  formHatasi.value = '';
  formAcik.value = true;
};

const duzenle = urun => {
  form.value = {
    id: urun.id,
    ad: urun.ad || '',
    kod: urun.kod || '',
    aciklama: urun.aciklama || '',
    // Kurus -> TL. Bu bolme atlanirsa kullanici 100 kat fazla fiyat gorur.
    fiyat: tlMetni(urun.fiyat_kurus),
    para_birimi: urun.para_birimi || 'TRY',
    stok: String(urun.stok ?? 0),
    durum: urun.durum || 'taslak',
    gorsel_url: urun.gorsel_url || '',
    urun_url: urun.urun_url || '',
    marka: urun.marka || '',
    kategori: urun.kategori || '',
  };
  formHatasi.value = '';
  formAcik.value = true;
};

const formuKapat = () => {
  formAcik.value = false;
  form.value = bosForm();
  formHatasi.value = '';
};

// Bos metni null gonderiyoruz: bos dize alani bosaltmaz, "" olarak saklanir.
const bosuNull = deger => {
  const metin = String(deger ?? '').trim();
  return metin === '' ? null : metin;
};

const kaydet = async () => {
  formHatasi.value = '';

  const ad = form.value.ad.trim();
  if (!ad) {
    formHatasi.value = t('KATALOG.FORM.AD_ZORUNLU');
    return;
  }

  // TL -> kurus. Sunucu kurus tamsayisi bekler.
  const fiyatKurus = kurusaCevir(form.value.fiyat);
  if (fiyatKurus === null) {
    formHatasi.value = t('KATALOG.FORM.FIYAT_GECERSIZ');
    return;
  }

  const stokMetni = String(form.value.stok ?? '').trim();
  const stok = stokMetni === '' ? 0 : Number(stokMetni);
  if (!Number.isInteger(stok) || stok < 0) {
    formHatasi.value = t('KATALOG.FORM.STOK_GECERSIZ');
    return;
  }

  const govde = {
    urun: {
      ad,
      kod: bosuNull(form.value.kod),
      aciklama: bosuNull(form.value.aciklama),
      fiyat_kurus: fiyatKurus,
      para_birimi: form.value.para_birimi || 'TRY',
      stok,
      durum: form.value.durum || 'taslak',
      gorsel_url: bosuNull(form.value.gorsel_url),
      urun_url: bosuNull(form.value.urun_url),
      marka: bosuNull(form.value.marka),
      kategori: bosuNull(form.value.kategori),
    },
  };

  kaydediliyor.value = true;
  try {
    if (form.value.id) {
      await axios.patch(`${uc.value}/${form.value.id}`, govde);
    } else {
      await axios.post(uc.value, govde);
    }
    useAlert(t('KATALOG.KAYDEDILDI'));
    formuKapat();
    await yukle();
  } catch (e) {
    formHatasi.value = hataMetni(e);
  } finally {
    kaydediliyor.value = false;
  }
};

const sil = async urun => {
  // Silme geri alinamaz; onay metninde bunu acikca yaziyoruz.
  if (!window.confirm(t('KATALOG.SIL_ONAY', { ad: urun.ad }))) return;

  islemdeki.value = urun.id;
  hata.value = '';
  try {
    await axios.delete(`${uc.value}/${urun.id}`);
    useAlert(t('KATALOG.SILINDI'));
    // Silinen urun duzenlemede aciksa panel bos kayitla acik kalmamali.
    if (form.value.id === urun.id) formuKapat();
    await yukle();
  } catch (e) {
    hata.value = hataMetni(e);
  } finally {
    islemdeki.value = null;
  }
};

const suzgeciTemizle = () => {
  arama.value = '';
  durumSuzgeci.value = '';
};

// Tek urunu Meta'ya gonderir. Toplu disa aktarim butun yayinda urunlere
// dokunuyor; kullanici tek bir urunu duzeltip yalnizca onu gondermek istiyor.
// Otomatik esitleme basarisiz olduysa elle tekrar denemek icin.
const simdiEsitle = async urun => {
  islemdeki.value = urun.id;
  hata.value = '';
  try {
    await axios.post(`${uc.value}/${urun.id}/meta-gonder`);
    useAlert(t('KATALOG.ESITLENDI', { ad: urun.ad }));
    await yukle();
  } catch (e) {
    hata.value = hataMetni(e);
  } finally {
    islemdeki.value = null;
  }
};

</script>

<template>
  <div class="p-6">
    <!-- Meta baglantisi zorunlu degil; kullanici eksik bir sey yaptigini
         sanmasin diye tonu bilgilendirici tutuyoruz. -->
    <p
      v-if="!metaBagli"
      class="flex items-start gap-2 p-3 mb-4 text-xs rounded-lg bg-n-alpha-1 text-n-slate-11"
    >
      <span class="mt-px shrink-0 i-lucide-info size-4" />
      <span>{{ t('KATALOG.META_BAGLI_DEGIL') }}</span>
    </p>

    <!-- Katalog bagliysa hangi katalogla calisildigi listenin ustunde
         yazmali: yanlis katalogu guncellemek musteriye yanlis urun
         gostermek demek. -->
    <p
      v-else-if="metaKatalog"
      class="flex items-start gap-2 p-3 mb-4 text-xs rounded-lg bg-n-alpha-1 text-n-slate-11"
    >
      <span class="mt-px shrink-0 i-ri-facebook-circle-fill size-4 text-n-blue-11" />
      <span>
        {{ t('KATALOG.META_KATALOG_BAGLI', { katalog: metaKatalog.ad }) }}
      </span>
    </p>

    <div class="flex flex-wrap items-center gap-2 mb-4">
      <label class="relative">
        <span class="sr-only">{{ t('KATALOG.ARAMA') }}</span>
        <input
          v-model="arama"
          type="search"
          class="!mb-0 min-w-[240px]"
          :placeholder="t('KATALOG.ARAMA_YER')"
        />
      </label>

      <label class="text-sm text-n-slate-11">
        <span class="sr-only">{{ t('KATALOG.SUTUN.DURUM') }}</span>
        <select v-model="durumSuzgeci" class="!mb-0">
          <option value="">{{ t('KATALOG.DURUM_HEPSI') }}</option>
          <option v-for="d in DURUMLAR" :key="d" :value="d">
            {{ t(DURUM_ANAHTARLARI[d]) }}
          </option>
        </select>
      </label>

      <span class="text-xs text-n-slate-11">
        <template v-if="suzgecVar">
          {{ t('KATALOG.SAYI_SUZGEC', { gosterilen: urunler.length, toplam: toplam }) }}
        </template>
        <template v-else>
          {{ t('KATALOG.SAYI', { sayi: toplam }) }}
        </template>
      </span>

      <span class="grow" />

      <Button
        size="sm"
        variant="faded"
        icon="i-lucide-refresh-cw"
        :label="t('KATALOG.YENILE')"
        @click="yukle"
      />
      <Button
        size="sm"
        variant="solid"
        icon="i-lucide-plus"
        :label="t('KATALOG.URUN_EKLE')"
        @click="yeniUrun"
      />
    </div>

    <p v-if="hata" class="mb-3 text-sm text-n-ruby-11">{{ hata }}</p>

    <!-- Form ayri sayfaya gitmiyor (listedeki yeri ve suzgeci
         kaybettirirdi) ama satir ici de degil: uzun listede
         "Duzenle"ye basildiginda form ekranin disinda kaliyor ve
         hicbir sey olmamis gibi gorunuyordu. Ustte duran bir
         pencere ikisini de cozuyor. -->
    <div
      v-if="formAcik"
      class="fixed inset-0 z-40 bg-black/50"
      @click="formuKapat"
    />
    <form
      v-if="formAcik"
      class="fixed inset-0 z-50 m-auto h-fit max-h-[85vh] w-[min(46rem,92vw)] overflow-y-auto rounded-xl border border-solid border-n-weak bg-n-solid-1 p-5 shadow-2xl"
      @submit.prevent="kaydet"
    >
      <h3 class="mb-3 text-sm font-medium text-n-slate-12">
        {{ form.id ? t('KATALOG.FORM.DUZENLE_BASLIK') : t('KATALOG.FORM.YENI_BASLIK') }}
      </h3>

      <div class="grid gap-3 sm:grid-cols-2">
        <label class="text-sm sm:col-span-2 text-n-slate-11">
          {{ t('KATALOG.FORM.AD') }}
          <input v-model="form.ad" type="text" required />
        </label>

        <label class="text-sm text-n-slate-11">
          {{ t('KATALOG.FORM.KOD') }}
          <input v-model="form.kod" type="text" />
          <span class="block mt-1 text-xs text-n-slate-11">
            {{ t('KATALOG.FORM.KOD_IPUCU') }}
          </span>
        </label>

        <label class="text-sm text-n-slate-11">
          {{ t('KATALOG.FORM.MARKA') }}
          <input v-model="form.marka" type="text" />
        </label>

        <label class="text-sm sm:col-span-2 text-n-slate-11">
          {{ t('KATALOG.FORM.ACIKLAMA') }}
          <textarea v-model="form.aciklama" rows="3" />
        </label>

        <label class="text-sm text-n-slate-11">
          {{ t('KATALOG.FORM.FIYAT') }}
          <!-- Metin alani: type="number" Turkce klavyedeki virgulu bos deger
               olarak okuyor, girilen fiyat kayboluyordu. -->
          <input
            v-model="form.fiyat"
            type="text"
            inputmode="decimal"
            placeholder="0,00"
          />
          <span class="block mt-1 text-xs text-n-slate-11">
            {{ t('KATALOG.FORM.FIYAT_IPUCU') }}
          </span>
        </label>

        <label class="text-sm text-n-slate-11">
          {{ t('KATALOG.FORM.PARA_BIRIMI') }}
          <select v-model="form.para_birimi">
            <option v-for="birim in PARA_BIRIMLERI" :key="birim" :value="birim">
              {{ birim }}
            </option>
          </select>
        </label>

        <label class="text-sm text-n-slate-11">
          {{ t('KATALOG.FORM.STOK') }}
          <input v-model="form.stok" type="number" min="0" step="1" />
        </label>

        <label class="text-sm text-n-slate-11">
          {{ t('KATALOG.FORM.DURUM') }}
          <select v-model="form.durum">
            <option v-for="d in DURUMLAR" :key="d" :value="d">
              {{ t(DURUM_ANAHTARLARI[d]) }}
            </option>
          </select>
          <span class="block mt-1 text-xs text-n-slate-11">
            {{ t('KATALOG.FORM.DURUM_IPUCU') }}
          </span>
        </label>

        <label class="text-sm text-n-slate-11">
          {{ t('KATALOG.FORM.GORSEL_URL') }}
          <input v-model="form.gorsel_url" type="text" placeholder="https://" />
        </label>

        <label class="text-sm text-n-slate-11">
          {{ t('KATALOG.FORM.URUN_URL') }}
          <input v-model="form.urun_url" type="text" placeholder="https://" />
        </label>

        <label class="text-sm text-n-slate-11">
          {{ t('KATALOG.FORM.KATEGORI') }}
          <input v-model="form.kategori" type="text" />
        </label>
      </div>

      <p v-if="formHatasi" class="mt-3 text-sm text-n-ruby-11">{{ formHatasi }}</p>

      <div class="flex gap-2 mt-4">
        <Button
          type="submit"
          size="sm"
          variant="solid"
          :is-loading="kaydediliyor"
          :label="t('KATALOG.FORM.KAYDET')"
        />
        <Button
          type="button"
          size="sm"
          variant="faded"
          :label="t('KATALOG.FORM.VAZGEC')"
          @click="formuKapat"
        />
      </div>
    </form>

    <div v-if="yukleniyor" class="flex justify-center p-10">
      <Spinner class="text-n-brand" />
    </div>

    <!-- Arama sonucu bos ile hic urun olmamasi ayri metinler: kullanici urun
         mu yok yoksa arama mi tutmadi ayirt edebilsin. -->
    <div
      v-else-if="!urunler.length && suzgecVar"
      class="p-10 text-center border rounded-lg border-n-weak"
    >
      <p class="text-sm text-n-slate-12">{{ t('KATALOG.ARAMA_BOS_BASLIK') }}</p>
      <p class="mt-1 mb-4 text-xs text-n-slate-11">
        {{ t('KATALOG.ARAMA_BOS_ACIKLAMA') }}
      </p>
      <Button
        size="sm"
        variant="faded"
        icon="i-lucide-x"
        :label="t('KATALOG.SUZGEC_TEMIZLE')"
        @click="suzgeciTemizle"
      />
    </div>

    <div
      v-else-if="!urunler.length"
      class="p-10 text-center border rounded-lg border-n-weak"
    >
      <p class="text-sm text-n-slate-12">{{ t('KATALOG.BOS_BASLIK') }}</p>
      <p class="mt-1 mb-4 text-xs text-n-slate-11">{{ t('KATALOG.BOS_ACIKLAMA') }}</p>
      <Button
        size="sm"
        variant="solid"
        icon="i-lucide-plus"
        :label="t('KATALOG.BOS_DUGME')"
        @click="yeniUrun"
      />
    </div>

    <template v-else>
      <table class="w-full text-sm">
        <thead class="text-left text-n-slate-11">
          <tr class="border-b border-n-weak">
            <th class="px-3 py-2 font-normal w-14">
              <span class="sr-only">{{ t('KATALOG.SUTUN.GORSEL') }}</span>
            </th>
            <th class="px-3 py-2 font-normal">{{ t('KATALOG.SUTUN.AD') }}</th>
            <th class="px-3 py-2 font-normal">{{ t('KATALOG.SUTUN.KOD') }}</th>
            <th class="px-3 py-2 font-normal">{{ t('KATALOG.SUTUN.FIYAT') }}</th>
            <th class="px-3 py-2 font-normal">{{ t('KATALOG.SUTUN.STOK') }}</th>
            <th class="px-3 py-2 font-normal">{{ t('KATALOG.SUTUN.DURUM') }}</th>
            <th class="px-3 py-2 font-normal">{{ t('KATALOG.SUTUN.META') }}</th>
            <th class="px-3 py-2 font-normal text-right">
              <span class="sr-only">{{ t('KATALOG.SUTUN.EYLEM') }}</span>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="urun in urunler"
            :key="urun.id"
            class="border-b border-n-weak hover:bg-n-alpha-1"
          >
            <td class="px-3 py-2.5 align-top">
              <div
                class="flex items-center justify-center overflow-hidden border rounded size-10 border-n-weak bg-n-alpha-1"
              >
                <img
                  v-if="gorselVar(urun)"
                  :src="urun.gorsel_url"
                  :alt="urun.ad"
                  class="object-cover size-full"
                  @error="gorselHatasi(urun)"
                />
                <span v-else class="i-lucide-image size-4 text-n-slate-11" />
              </div>
            </td>
            <td class="px-3 py-2.5 align-top text-n-slate-12">
              <a
                v-if="urun.urun_url"
                :href="urun.urun_url"
                target="_blank"
                rel="noopener noreferrer"
                class="hover:underline"
              >
                {{ urun.ad }}
              </a>
              <template v-else>{{ urun.ad }}</template>
              <span v-if="urun.marka" class="block text-xs text-n-slate-11">
                {{ urun.marka }}
              </span>
            </td>
            <td class="px-3 py-2.5 align-top text-n-slate-11">{{ urun.kod || '—' }}</td>
            <td class="px-3 py-2.5 align-top whitespace-nowrap text-n-slate-12">
              {{ fiyatMetni(urun) }}
            </td>
            <td class="px-3 py-2.5 align-top text-n-slate-12">{{ urun.stok ?? 0 }}</td>
            <td class="px-3 py-2.5 align-top">
              <span
                class="inline-block px-2 py-0.5 text-xs rounded"
                :class="DURUM_RENKLERI[urun.durum] || 'text-n-slate-11 bg-n-alpha-1'"
              >
                {{
                  DURUM_ANAHTARLARI[urun.durum]
                    ? t(DURUM_ANAHTARLARI[urun.durum])
                    : urun.durum
                }}
              </span>
            </td>
            <td class="px-3 py-2.5 align-top whitespace-nowrap">
              <!-- Esitleme durumu. Yesil tik = Meta'daki hal ile birebir ayni.
                   "Metada var" demek yetmiyordu: urun Meta'da olsa bile yerel
                   duzenleme gonderilmemis olabiliyor. -->
              <span
                v-if="urun.meta_durum === 'esit'"
                class="inline-flex items-center gap-1 text-xs text-n-teal-11"
                :title="
                  urun.meta_senkron_zamani
                    ? t('KATALOG.META_ZAMAN', { zaman: zamanMetni(urun.meta_senkron_zamani) })
                    : ''
                "
              >
                <span class="i-lucide-check-circle-2 size-3.5" />
                {{ t('KATALOG.META_DURUM.ESIT') }}
              </span>
              <span
                v-else-if="urun.meta_durum === 'bekliyor'"
                class="inline-flex items-center gap-1 text-xs text-n-amber-11"
              >
                <span class="i-lucide-refresh-cw size-3.5" />
                {{ t('KATALOG.META_DURUM.BEKLIYOR') }}
              </span>
              <span
                v-else-if="urun.meta_durum === 'hata'"
                class="inline-flex items-center gap-1 text-xs text-n-ruby-11"
                :title="urun.meta_senkron_hatasi || ''"
              >
                <span class="i-lucide-triangle-alert size-3.5" />
                {{ t('KATALOG.META_DURUM.HATA') }}
              </span>
              <span v-else class="text-xs text-n-slate-11">
                {{ t('KATALOG.META_DURUM.YOK') }}
              </span>
            </td>
            <td class="px-3 py-2.5 text-right align-top whitespace-nowrap">
              <div class="flex items-center justify-end gap-1">
                <!-- Esit olan urunde dugme YOK: yapacak bir sey kalmadi.
                     Yalnizca esitlenmemis ya da hatali urunde cikar. -->
                <Button
                  v-if="
                    metaKatalog &&
                    urun.durum === 'yayinda' &&
                    ['bekliyor', 'hata', 'yok'].includes(urun.meta_durum)
                  "
                  size="sm"
                  variant="faded"
                  icon="i-lucide-refresh-cw"
                  :label="t('KATALOG.SIMDI_ESITLE')"
                  :is-loading="islemdeki === urun.id"
                  :disabled="islemdeki === urun.id"
                  @click="simdiEsitle(urun)"
                />
                <Button
                  size="sm"
                  variant="faded"
                  :label="t('KATALOG.DUZENLE')"
                  :disabled="islemdeki === urun.id"
                  @click="duzenle(urun)"
                />
                <button
                  type="button"
                  class="px-2 py-1 text-xs rounded text-n-ruby-11 hover:bg-n-ruby-3 disabled:opacity-50"
                  :disabled="islemdeki === urun.id"
                  @click="sil(urun)"
                >
                  {{ t('KATALOG.SIL') }}
                </button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>

      <p v-if="urunler.length >= SUNUCU_SINIRI" class="mt-3 text-xs text-n-slate-11">
        {{ t('KATALOG.SINIR_UYARI', { sayi: SUNUCU_SINIRI }) }}
      </p>
    </template>
  </div>
</template>
